import axios from 'axios';
import type { DateTime } from 'luxon';

export default class ArticlesService
{
    public static async getList(filter: FilterModel, pager: Pager)
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/cms/articles', {
            params: { ...filter, ...pager.data() }
        })).data;
    }

    public static async getListByIds(articles: number[])
    {
        return (await axios.get<ListItemModel[]>('admin/cms/articles/get-by-ids', {
            params: {
                articles: articles.join(',')
            }
        })).data;
    }

    public static async getListByCategoryId(categoryId: number)
    {
        return (await axios.get<ListItemModel[]>(`admin/cms/articles/category/${categoryId}`)).data;
    }

    public static async getListByAuthorId(authorId: number)
    {
        return (await axios.get<ListItemModel[]>(`admin/cms/articles/author/${authorId}`)).data;
    }

    public static async fetch(id: number)
    {
        return (await axios.get<Resource<FormModel>>(`admin/cms/articles/${id}`)).data;
    }

    public static async getRelated(id: number)
    {
        return (await axios.get<ListItemModel[]>(`admin/cms/articles/${id}/related`)).data;
    }

    public static async getRelatedFromWidget(id: number, showType: boolean = false)
    {
        return (await axios.get<ListItemModel[]>(`admin/cms/articles/${id}/relatedFromWidget`, {
            params: {
                showType: showType
            }
        })).data;
    }

    public static async getNewArticles(websiteId: number, locale: string, limit: number)
    {
        return (await axios.get<NewArticlesModel[]>(`admin/dashboards/widgets/new-articles`, {
            params: {
                websiteId: websiteId,
                locale: locale,
                limit: limit
            }
        })).data;
    }

    public static async getPopularArticles(websiteId: number, locale: string, getMostViewed: boolean, limit: number)
    {
        return (await axios.get<PopularArticlesModel[]>(`admin/dashboards/widgets/most-popular`, {
            params: {
                websiteId: websiteId,
                locale: locale,
                getMostViewed: getMostViewed,
                limit: limit
            }
        })).data;
    }

    public static async getViewedArticles(websiteId: number, locale: string, limit: number)
    {
        return (await axios.get<ViewedArticlesModel[]>(`admin/dashboards/widgets/most-viewed`, {
            params: {
                websiteId: websiteId,
                locale: locale,
                limit: limit
            }
        })).data;
    }

    public static async getPremiumFromPackage(id: number, showType: boolean = false)
    {
        return (await axios.get<ListItemModel[]>(`admin/cms/articles/${id}/premiumFromPackage`, {
            params: {
                showType: showType
            }
        })).data;
    }

    public static async create(model: UpsertModel)
    {
        return (await axios.post<Resource<FormModel>>(`admin/cms/articles`, model)).data;
    }

    public static async update(id: number, model: UpsertModel)
    {
        return (await axios.put<Resource<FormModel>>(`admin/cms/articles/${id}`, model)).data;
    }

    public static async remove(id: number)
    {
        return (await axios.delete(`admin/cms/articles/${id}`)).data as Statement;
    }

    public static async status(id: number, status: PublicationEnum)
    {
        return (await axios.patch(`admin/cms/articles/${id}/status`, {
            status: status
        })).data;
    }

    public static async publish(id: number, model: PublishModel)
    {
        return (await axios.patch(`admin/cms/articles/${id}/publish`, model)).data;
    }

    public static async attach(id: number, pageId: number)
    {
        return (await axios.post(`admin/cms/articles/${id}/page/${pageId}`)).data;
    }

    public static async detach(id: number, pageId: number)
    {
        return (await axios.delete(`admin/cms/articles/${id}/page/${pageId}`)).data;
    }

    public static async getVersions(id: number, pager: Pager)
    {
        return (await axios.get<Pagination<EntityVersion>>(`admin/cms/articles/${id}/versions`, {
            params: pager.data()
        })).data;
    }

    public static async getPagePublications(id: number)
    {
        return (await axios.get<PagePublication[]>(`admin/cms/articles/${id}/pages`)).data;
    }

    public static async getAutocompleteList(
        search: string,
        limit: number = 10,
        pageId: number = 0,
        articleIds: number[] = [],
        showType: boolean = false,
        onlyOpinions: boolean = false)
    {
        return (await axios.get<ListItemModel[]>('admin/cms/articles/autocomplete', {
            params: {
                search: search,
                limit: limit,
                pageId: pageId,
                articleIds: articleIds,
                showType: showType,
                onlyOpinions: onlyOpinions
            }
        })).data;
    }

    public static async fetchAutocompleteModel(id: number, showType: boolean = false, onlyOpinions: boolean = false)
    {
        return (await axios.get<ListItemModel>(`admin/cms/articles/autocomplete/${id}`, {
            params: {
                showType: showType,
                onlyOpinions: onlyOpinions
            }
        })).data;
    }

    public static async fetchAutocompleteModelByOriginId(originId: number, showType: boolean = false, onlyOpinions: boolean = false)
    {
        return (await axios.get<ListItemModel>(`admin/cms/articles/autocomplete/${originId}/origin`, {
            params: {
                showType: showType,
                onlyOpinions: onlyOpinions
            }
        })).data;
    }

    public static async getAutocompletePremiumList(search: string, limit: number = 10)
    {
        return (await axios.get<ListItemModel[]>('admin/cms/articles/autocomplete/premium', {
            params: {
                search: search,
                limit: limit
            }
        })).data;
    }
}

export interface ListItemModel
{
    authors: string;
    id: number;
    dateCreatedUtc: DateTime;
    dateModifiedUtc: DateTime;
    createdBy: string;
    modifiedBy: string;
    title: string;
    status: PublicationEnum;
    originId: number;
    isArchive: boolean;
    labelName: string;
    labelTextColor: string;
    labelBackgroundColor: string;
}

export interface FilterModel
{
    authorId?: number;
    pageId?: number;
    userId: number;
    status: PublicationEnum;
    title: string;
    dateFromUtc?: DateTime;
    dateDueUtc?: DateTime;
    onlyMine: boolean;
    onlyTeam: boolean;
    isOpinion: boolean;
    isPublic: boolean;
    isSponsored: boolean;
    isPremium: boolean;
    contentOnly: boolean;
    shareDisabled: boolean;
    commentsDisabled: boolean;
    fullPageView: boolean;
    isOpenPremium: boolean;
}

export interface UpsertModel
{
    id: number;
    originId: number;
    title: string;
    shortTitle: string;
    symbol: string;
    tags: string[];
    lead: string;
    content: string[];
    picture?: FileResource;
    attachments: FileResource[];
    contentOnly: boolean;
    meta: MetaSettingsModel | null;
    photoGallery: FileResource[];
    video?: FileResource;
    videoCover?: FileResource;
    videoUrl?: string;
    relatedArticles: RelatedModel[];
    authors: number[];
    preTitle: string;
    surveyId: number;
    isSurveyOn: boolean;
    isPremium: boolean;
    isOpenPremium: boolean;
    premiumAttachments: FileResource[];
    premiumPhotoGallery: FileResource[];
    premiumVideo?: FileResource;
    premiumVideoCover?: FileResource;
    premiumVideoUrl?: string;
    isOpinion: boolean;
    isSponsored: boolean;
    commentsDisabled: boolean;
    shareDisabled: boolean;
    labelId?: number;
    categories: string[];
    fullPageView: boolean;
    isContactFormOn: boolean;
    contactFormCategoryId?: number;
    contactFormId?: number;
    articleSource: string;
    priority: boolean;
    internalLinksIds: number[];
}

export interface FormModel extends UpsertModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
    status: PublicationEnum;
    locale: string;
}

export interface PublishModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
}

export interface RelatedModel
{
    id: number;
    section: RelatedArticleSectionsEnum | number;
}

export interface NewArticlesModel
{
    id: number;
    dateCreatedUtc: DateTime;
    title: string;
    picture?: FileResource;
    views: number;
}

export interface PopularArticlesModel
{
    id: number;
    title: string;
    picture?: FileResource;
    likes: number;
    views: number;
}

export interface ViewedArticlesModel
{
    id: number;
    title: string;
    picture?: FileResource;
    views: number;
}
